import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { QuotationComponent } from './features/wizardcomponent/quotation/quotation.component';
import { DataInfoComponent } from './features/wizardcomponent/data-info/data-info.component';
import { PaymentComponent } from './features/wizardcomponent/payment/payment.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideTranslateService, TranslateModule } from '@ngx-translate/core';
import { HeaderComponent } from './shared/header/header.component';
import { TitleComponent } from './shared/title/title.component';
import { AutocompleteComponent } from './shared/autocomplete/autocomplete.component';
import { ButtonBasicComponent } from './shared/button-basic/button-basic.component';
import { CoverageDetailComponent } from './shared/coverage-detail/coverage-detail.component';
import { CoverageInfoComponent } from './shared/coverage-info/coverage-info.component';
import { ErrorDialogComponent } from './shared/dialog/error-dialog/error-dialog.component';
import { DynamicFormComponent } from './shared/dynamic-form/dynamic-form.component';
import { TotalPriceResumeComponent } from './shared/total-price-resume/total-price-resume.component';
import { MatIconModule } from '@angular/material/icon';
import { APP_BASE_HREF, CommonModule, PlatformLocation} from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { SafeHtmlPipe } from './shared/pipe/safe-html.pipe';
import { BlueHelpCardComponent } from './shared/blue-help-card/blue-help-card.component';
import { DynamicFieldDirective } from './shared/directives/dynamic-field/dynamic-field.directive';
import { MatDividerModule } from '@angular/material/divider';
import { WizardComponent } from './features/wizardcomponent/wizard.component';
import { BooleanSelectionComponent } from './shared/boolean-selection/boolean-selection.component';
import { InputComponent } from './shared/input/input.component';
import { DateInputComponent } from './shared/date-input/date-input.component';
import { CheckboxComponent } from './shared/checkbox/checkbox.component';
import { SelectComponent } from './shared/select/select.component';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateFilterPipe } from './shared/pipe/date-filter.pipe';
import { MatInputModule } from '@angular/material/input';
import { DateAdapter, MAT_DATE_LOCALE, MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule, MomentDateAdapter, provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ConfirmDialogComponent } from './shared/dialog/confirm-dialog/confirm-dialog.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SpinnerComponent } from './shared/spinner/spinner.component';
import { MatDialogModule } from '@angular/material/dialog';
import { CoverageModalBaseComponent } from './shared/coverage-modal-base/coverage-modal-base.component';
import { IconInformationComponent } from './shared/icon-information/icon-information.component';
import { DropdownInfoComponent } from './shared/dropdown-info/dropdown-info.component';
import { RouterModule } from '@angular/router';
import { InputGoogleComponent } from './shared/input-google/input-google.component';
import { ErrorInterceptor } from './core/interceptors/error.incerceptor';


/**
 * This function is used internal to get a string instance of the `<base href="" />` value from `index.html`.
 * This is an exported function, instead of a private function or inline lambda, to prevent this error:
 *
 * `Error encountered resolving symbol values statically.`
 * `Function calls are not supported.`
 * `Consider replacing the function or lambda with a reference to an exported function.`
 *
 * @param platformLocation an Angular service used to interact with a browser's URL
 * @return a string instance of the `<base href="" />` value from `index.html`
 */
export function getBaseHref(platformLocation: PlatformLocation): string {
  return platformLocation.getBaseHrefFromDOM();
}

declare global {
  interface Window {
    Cookiebot: any;
  }
}

@NgModule({
  declarations: [
    AppComponent,
    WizardComponent,
    QuotationComponent,
    HeaderComponent,
    DataInfoComponent,
    PaymentComponent,
    ButtonBasicComponent,
    AutocompleteComponent,
    CoverageDetailComponent,
    CoverageInfoComponent,
    ErrorDialogComponent,
    DynamicFormComponent,
    TotalPriceResumeComponent,
    TitleComponent,
    SafeHtmlPipe,
    BlueHelpCardComponent,
    BooleanSelectionComponent,
    InputComponent,
    DateInputComponent,
    CheckboxComponent,
    SelectComponent,
    DateFilterPipe,
    DynamicFieldDirective,
    ConfirmDialogComponent,
    SpinnerComponent,
    CoverageModalBaseComponent,
    IconInformationComponent,
    DropdownInfoComponent,
    InputGoogleComponent
  ],
  imports: [
    BrowserModule,
    RouterModule,
    MatIconModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    CommonModule,
    FormsModule,
    HttpClientModule,
    AppRoutingModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatSelectModule,
    MatDatepickerModule,
    MatInputModule,
    MatNativeDateModule,
    MatMomentDateModule,
    BrowserAnimationsModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ],
  providers: [
    provideNativeDateAdapter(),
    provideAnimationsAsync(),
    provideTranslateService({
      fallbackLang: 'es',
      lang: 'es'
    }),
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: APP_BASE_HREF, useFactory: getBaseHref, deps: [PlatformLocation] },
    { provide: LOCALE_ID, useValue: 'es-ES' },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
